import type { ShapeType } from "./types";
import { SHAPE_BASE_SIZE_RATIO } from "./types";

/**
 * Canonical definition of the mesh-* 3D shape primitives.
 *
 * Two renderers draw these clips: the preview (three.js, in
 * apps/web/src/components/editor/preview/threejs-layer-renderer.ts) and the
 * export (the software rasterizer in graphics-engine.ts, which cannot use
 * three.js because it draws into the same 2D context as every other overlay).
 * Both read their dimensions, tessellation and lighting from this module so the
 * two paths cannot drift: a cube that is 162px wide in the preview is 162px wide
 * in the exported file.
 *
 * Conventions used throughout:
 * - Right-handed space, +Y up, +Z toward the viewer, matching three.js.
 * - Units are canvas pixels. The preview camera is orthographic with a frustum
 *   the size of the canvas, so one world unit is one pixel in both paths.
 * - Faces are wound counter-clockwise seen from outside the solid, so the
 *   normal computed from the first three vertices points outward.
 */

export const MESH_SHAPE_TYPES = [
  "mesh-cube",
  "mesh-sphere",
  "mesh-torus",
  "mesh-cone",
  "mesh-cylinder",
  "mesh-icosahedron",
] as const;

export type MeshShapeType = (typeof MESH_SHAPE_TYPES)[number];

export function isMeshShapeType(
  shapeType: ShapeType,
): shapeType is MeshShapeType {
  return (MESH_SHAPE_TYPES as readonly string[]).includes(shapeType);
}

export interface MeshVec3 {
  readonly x: number;
  readonly y: number;
  readonly z: number;
}

export interface MeshPrimitive {
  /** Vertex positions in local space, centered on the origin. */
  readonly vertices: readonly MeshVec3[];
  /** Indices into `vertices`, counter-clockwise seen from outside. */
  readonly faces: readonly (readonly number[])[];
}

/**
 * Dimensions every mesh primitive is built from, derived from one `baseSize`.
 *
 * `baseSize` is min(canvasWidth, canvasHeight) * SHAPE_BASE_SIZE_RATIO, the same
 * figure the 2D shape renderer uses for its shapes, so switching a clip between
 * a 2D and a 3D shape does not jump its on-screen size.
 */
export interface MeshPrimitiveDimensions {
  /** Cube edge length, and the height of the cone and the cylinder. */
  readonly edge: number;
  /** Sphere, torus, cone, cylinder and icosahedron radius. */
  readonly radius: number;
  /** Torus tube (minor) radius. */
  readonly tubeRadius: number;
}

export function getMeshPrimitiveDimensions(
  baseSize: number,
): MeshPrimitiveDimensions {
  return {
    edge: baseSize,
    radius: baseSize / 2,
    tubeRadius: baseSize / 6,
  };
}

/** Tessellation shared by both renderers. */
export const MESH_PRIMITIVE_SEGMENTS = {
  sphereWidth: 32,
  sphereHeight: 32,
  torusRadial: 16,
  torusTubular: 48,
  coneRadial: 32,
  cylinderRadial: 32,
} as const;

/** Fill used when a mesh clip has no explicit fill color. */
export const MESH_DEFAULT_FILL_COLOR = "#3b82f6";

/**
 * Lighting rig, mirroring the preview scene: one white ambient light at 0.7 and
 * one white directional light at 0.6 coming from (0.5, 1, 1).
 *
 * The preview shades meshes with a three.js physically based material, which
 * this Lambert term only approximates: the export is a close match in tone, not
 * a pixel-identical copy of the GPU render.
 */
export const MESH_AMBIENT_INTENSITY = 0.7;
export const MESH_DIRECTIONAL_INTENSITY = 0.6;
export const MESH_LIGHT_DIRECTION: MeshVec3 = normalizeVector({
  x: 0.5,
  y: 1,
  z: 1,
});

export function baseSizeForCanvas(width: number, height: number): number {
  return Math.min(width, height) * SHAPE_BASE_SIZE_RATIO;
}

export function buildMeshPrimitive(
  shapeType: MeshShapeType,
  baseSize: number,
): MeshPrimitive {
  const dims = getMeshPrimitiveDimensions(baseSize);

  switch (shapeType) {
    case "mesh-cube":
      return buildBox(dims.edge);
    case "mesh-sphere":
      return buildSphere(
        dims.radius,
        MESH_PRIMITIVE_SEGMENTS.sphereWidth,
        MESH_PRIMITIVE_SEGMENTS.sphereHeight,
      );
    case "mesh-torus":
      return buildTorus(
        dims.radius,
        dims.tubeRadius,
        MESH_PRIMITIVE_SEGMENTS.torusRadial,
        MESH_PRIMITIVE_SEGMENTS.torusTubular,
      );
    case "mesh-cone":
      return buildCone(
        dims.radius,
        dims.edge,
        MESH_PRIMITIVE_SEGMENTS.coneRadial,
      );
    case "mesh-cylinder":
      return buildCylinder(
        dims.radius,
        dims.edge,
        MESH_PRIMITIVE_SEGMENTS.cylinderRadial,
      );
    case "mesh-icosahedron":
      return buildIcosahedron(dims.radius);
  }
}

function buildBox(edge: number): MeshPrimitive {
  const h = edge / 2;
  const vertices: MeshVec3[] = [
    { x: -h, y: -h, z: h },
    { x: h, y: -h, z: h },
    { x: h, y: h, z: h },
    { x: -h, y: h, z: h },
    { x: -h, y: -h, z: -h },
    { x: h, y: -h, z: -h },
    { x: h, y: h, z: -h },
    { x: -h, y: h, z: -h },
  ];

  const faces = [
    [0, 1, 2, 3],
    [5, 4, 7, 6],
    [1, 5, 6, 2],
    [4, 0, 3, 7],
    [3, 2, 6, 7],
    [4, 5, 1, 0],
  ];

  return { vertices, faces };
}

function buildSphere(
  radius: number,
  widthSegments: number,
  heightSegments: number,
): MeshPrimitive {
  const vertices: MeshVec3[] = [];

  for (let iy = 0; iy <= heightSegments; iy++) {
    const theta = (iy / heightSegments) * Math.PI;
    const sinTheta = Math.sin(theta);
    const cosTheta = Math.cos(theta);

    for (let ix = 0; ix <= widthSegments; ix++) {
      const phi = (ix / widthSegments) * Math.PI * 2;
      vertices.push({
        x: radius * sinTheta * Math.cos(phi),
        y: radius * cosTheta,
        z: radius * sinTheta * Math.sin(phi),
      });
    }
  }

  const faces: number[][] = [];
  const rowStride = widthSegments + 1;

  for (let iy = 0; iy < heightSegments; iy++) {
    for (let ix = 0; ix < widthSegments; ix++) {
      const a = iy * rowStride + ix;
      const b = a + 1;
      const c = (iy + 1) * rowStride + ix + 1;
      const d = (iy + 1) * rowStride + ix;

      // The pole rows collapse to a point, so they emit triangles.
      if (iy === 0) {
        faces.push([a, c, d]);
      } else if (iy === heightSegments - 1) {
        faces.push([a, b, c]);
      } else {
        faces.push([a, b, c, d]);
      }
    }
  }

  return { vertices, faces };
}

function buildTorus(
  radius: number,
  tube: number,
  radialSegments: number,
  tubularSegments: number,
): MeshPrimitive {
  const vertices: MeshVec3[] = [];

  for (let j = 0; j <= radialSegments; j++) {
    const v = (j / radialSegments) * Math.PI * 2;
    for (let i = 0; i <= tubularSegments; i++) {
      const u = (i / tubularSegments) * Math.PI * 2;
      const ringRadius = radius + tube * Math.cos(v);
      vertices.push({
        x: ringRadius * Math.cos(u),
        y: ringRadius * Math.sin(u),
        z: tube * Math.sin(v),
      });
    }
  }

  const faces: number[][] = [];
  const rowStride = tubularSegments + 1;

  for (let j = 0; j < radialSegments; j++) {
    for (let i = 0; i < tubularSegments; i++) {
      const a = j * rowStride + i;
      const b = a + 1;
      const c = (j + 1) * rowStride + i + 1;
      const d = (j + 1) * rowStride + i;
      faces.push([a, b, c, d]);
    }
  }

  return { vertices, faces };
}

function buildCone(
  radius: number,
  height: number,
  radialSegments: number,
): MeshPrimitive {
  const halfHeight = height / 2;
  const vertices: MeshVec3[] = [
    { x: 0, y: halfHeight, z: 0 },
    { x: 0, y: -halfHeight, z: 0 },
  ];
  const apex = 0;
  const baseCenter = 1;

  for (let i = 0; i < radialSegments; i++) {
    const angle = (i / radialSegments) * Math.PI * 2;
    vertices.push({
      x: radius * Math.sin(angle),
      y: -halfHeight,
      z: radius * Math.cos(angle),
    });
  }

  const faces: number[][] = [];
  for (let i = 0; i < radialSegments; i++) {
    const current = 2 + i;
    const next = 2 + ((i + 1) % radialSegments);
    faces.push([apex, current, next]);
    faces.push([baseCenter, next, current]);
  }

  return { vertices, faces };
}

function buildCylinder(
  radius: number,
  height: number,
  radialSegments: number,
): MeshPrimitive {
  const halfHeight = height / 2;
  const vertices: MeshVec3[] = [
    { x: 0, y: halfHeight, z: 0 },
    { x: 0, y: -halfHeight, z: 0 },
  ];
  const topCenter = 0;
  const bottomCenter = 1;

  for (let i = 0; i < radialSegments; i++) {
    const angle = (i / radialSegments) * Math.PI * 2;
    const x = radius * Math.sin(angle);
    const z = radius * Math.cos(angle);
    vertices.push({ x, y: halfHeight, z });
    vertices.push({ x, y: -halfHeight, z });
  }

  const faces: number[][] = [];
  for (let i = 0; i < radialSegments; i++) {
    const topCurrent = 2 + i * 2;
    const bottomCurrent = topCurrent + 1;
    const topNext = 2 + ((i + 1) % radialSegments) * 2;
    const bottomNext = topNext + 1;

    faces.push([topCurrent, bottomCurrent, bottomNext, topNext]);
    faces.push([topCenter, topCurrent, topNext]);
    faces.push([bottomCenter, bottomNext, bottomCurrent]);
  }

  return { vertices, faces };
}

function buildIcosahedron(radius: number): MeshPrimitive {
  const t = (1 + Math.sqrt(5)) / 2;
  const raw: MeshVec3[] = [
    { x: -1, y: t, z: 0 },
    { x: 1, y: t, z: 0 },
    { x: -1, y: -t, z: 0 },
    { x: 1, y: -t, z: 0 },
    { x: 0, y: -1, z: t },
    { x: 0, y: 1, z: t },
    { x: 0, y: -1, z: -t },
    { x: 0, y: 1, z: -t },
    { x: t, y: 0, z: -1 },
    { x: t, y: 0, z: 1 },
    { x: -t, y: 0, z: -1 },
    { x: -t, y: 0, z: 1 },
  ];

  const vertices = raw.map((v) => scaleVector(normalizeVector(v), radius));

  const faces = [
    [0, 11, 5],
    [0, 5, 1],
    [0, 1, 7],
    [0, 7, 10],
    [0, 10, 11],
    [1, 5, 9],
    [5, 11, 4],
    [11, 10, 2],
    [10, 7, 6],
    [7, 1, 8],
    [3, 9, 4],
    [3, 4, 2],
    [3, 2, 6],
    [3, 6, 8],
    [3, 8, 9],
    [4, 9, 5],
    [2, 4, 11],
    [6, 2, 10],
    [8, 6, 7],
    [9, 8, 1],
  ];

  return { vertices, faces };
}

function normalizeVector(v: MeshVec3): MeshVec3 {
  const length = Math.hypot(v.x, v.y, v.z);
  if (length === 0) return { x: 0, y: 0, z: 0 };
  return { x: v.x / length, y: v.y / length, z: v.z / length };
}

function scaleVector(v: MeshVec3, factor: number): MeshVec3 {
  return { x: v.x * factor, y: v.y * factor, z: v.z * factor };
}

/**
 * Rotates by Euler angles (radians) in three.js' default XYZ order, so a clip's
 * rotate3d values produce the same orientation in the export as in the preview.
 */
export function rotateEulerXYZ(
  v: MeshVec3,
  angleX: number,
  angleY: number,
  angleZ: number,
): MeshVec3 {
  const cz = Math.cos(angleZ);
  const sz = Math.sin(angleZ);
  const x1 = v.x * cz - v.y * sz;
  const y1 = v.x * sz + v.y * cz;
  const z1 = v.z;

  const cy = Math.cos(angleY);
  const sy = Math.sin(angleY);
  const x2 = x1 * cy + z1 * sy;
  const y2 = y1;
  const z2 = -x1 * sy + z1 * cy;

  const cx = Math.cos(angleX);
  const sx = Math.sin(angleX);
  return {
    x: x2,
    y: y2 * cx - z2 * sx,
    z: y2 * sx + z2 * cx,
  };
}

/** Face normal from the first three vertices, pointing out of the solid. */
export function meshFaceNormal(a: MeshVec3, b: MeshVec3, c: MeshVec3): MeshVec3 {
  const ux = b.x - a.x;
  const uy = b.y - a.y;
  const uz = b.z - a.z;
  const vx = c.x - a.x;
  const vy = c.y - a.y;
  const vz = c.z - a.z;

  return normalizeVector({
    x: uy * vz - uz * vy,
    y: uz * vx - ux * vz,
    z: ux * vy - uy * vx,
  });
}

/**
 * Lambert term for a face normal under the shared lighting rig. Range is
 * [MESH_AMBIENT_INTENSITY, MESH_AMBIENT_INTENSITY + MESH_DIRECTIONAL_INTENSITY].
 */
export function meshFaceLightIntensity(normal: MeshVec3): number {
  const lambert = Math.max(
    0,
    normal.x * MESH_LIGHT_DIRECTION.x +
      normal.y * MESH_LIGHT_DIRECTION.y +
      normal.z * MESH_LIGHT_DIRECTION.z,
  );
  return MESH_AMBIENT_INTENSITY + MESH_DIRECTIONAL_INTENSITY * lambert;
}

export interface MeshRgb {
  readonly r: number;
  readonly g: number;
  readonly b: number;
}

/** Parses #rgb / #rrggbb. Returns null for anything else so callers can fall back. */
export function parseMeshColor(hex: string): MeshRgb | null {
  let value = hex.trim().replace("#", "");
  if (value.length === 3) {
    value = value
      .split("")
      .map((c) => c + c)
      .join("");
  }
  if (value.length !== 6 || !/^[0-9a-fA-F]{6}$/.test(value)) return null;

  return {
    r: parseInt(value.slice(0, 2), 16),
    g: parseInt(value.slice(2, 4), 16),
    b: parseInt(value.slice(4, 6), 16),
  };
}

/** Applies a light intensity to a color and formats it for a canvas fillStyle. */
export function shadeMeshColor(color: MeshRgb, intensity: number): string {
  const channel = (value: number): number =>
    Math.max(0, Math.min(255, Math.round(value * intensity)));
  return `rgb(${channel(color.r)}, ${channel(color.g)}, ${channel(color.b)})`;
}
